import { geo } from '../tokens.js';

export interface StageFit {
  /** Uniform scale applied to the 1440 × 900 stage. */
  readonly scale: number;
  /** Only the stage's central band is sure to show: its sides may be cut off at the window's edges. */
  readonly narrow: boolean;
  /** Size of the box that holds the scaled stage, in CSS pixels. */
  readonly width: number;
  readonly height: number;
  /** Left offset of the scaled stage inside that box (negative when the sides are cropped). */
  readonly offsetX: number;
  /** Top margin that centres the box when the window is taller than it. */
  readonly marginTop: number;
}

/** Below this window width the stage's central band may be all that shows: see `fitStage`. */
export const NARROW_BELOW = 1024;
/** Below this window width the window is a phone held upright: its height never limits the scale. */
export const PHONE_BELOW = 600;
/** The band a narrow window is fitted to: the 560 px column with 8 px of stage on each side, 8 px from the window's edges. */
const BAND = 576, GUTTER = 8;
/** The whole stage is not drawn smaller than this; a window smaller than 720 × 450 scrolls. */
const WHOLE_FLOOR = 0.5;

/**
 * How the fixed stage fits a window. Implementation default (not from the prototype; 08 §2.4 called its
 * first version a stop-gap). Owner, 4 October 2026: a laptop whose window is short showed the stage far too
 * large, and a phone should show the calendar a little bigger. The stage is never taller than the window
 * unless the window is a phone or smaller than 720 × 450:
 * - under 600 px wide (a phone): the central band fitted to the width, `(vw − 16) / 576`, centred, with
 *   vertical scrolling. The height is no limit, so a keyboard that shortens the window never shrinks the page;
 * - 600 to 1023 px wide: the band fitted to the width and the height, `min(1, (vw − 16) / 576, vh / 900)`,
 *   unless the whole stage fits just as large: then the whole stage shows;
 * - from 1024 px: the whole stage, `min(1, vw / 1440, vh / 900)`, centred; below 0.5 the page scrolls.
 * No phone layout was designed: the band is what a phone shows of the desktop stage.
 */
export function fitStage(viewportWidth: number, viewportHeight: number): StageFit {
  const vw = Math.max(1, viewportWidth), vh = Math.max(1, viewportHeight);
  const whole = Math.min(1, vw / geo.stageW, vh / geo.stageH);
  const across = Math.min(1, (vw - 2 * GUTTER) / BAND);
  const band = vw < PHONE_BELOW ? across : Math.min(across, vh / geo.stageH);
  if (vw < NARROW_BELOW && band > whole) {
    const scale = Math.max(0.1, band), height = geo.stageH * scale;
    return { scale, narrow: true, width: vw, height, offsetX: (vw - geo.stageW * scale) / 2, marginTop: Math.max(0, (vh - height) / 2) };
  }
  const scale = Math.max(WHOLE_FLOOR, whole);
  const width = geo.stageW * scale, height = geo.stageH * scale;
  return { scale, narrow: false, width, height, offsetX: 0, marginTop: Math.max(0, (vh - height) / 2) };
}
